// /help — index landing page (CTSK-356).
//
// Replaces the v1 "render the repo README" behavior at the bare
// `/help` route with a browsable catalog of every shipped help doc,
// grouped by area. Individual pages are unchanged: `/help/<slug>`
// still renders through pages/Help.tsx.
//
// Data comes from lib/help-catalog.ts (metadata-only single source of
// truth). Areas render in first-appearance order from the catalog;
// entries keep their catalog order within each area.

import { HELP_PAGES, type HelpPageMeta } from '../lib/help-catalog.js';

export interface HelpIndexProps {
  /** SPA navigation callback (same contract as Help / AppFooter). */
  onNavigate?: (path: string) => void;
  /** Override the catalog — tests inject fixtures; production omits. */
  pages?: readonly HelpPageMeta[];
}

export function HelpIndex({ onNavigate, pages = HELP_PAGES }: HelpIndexProps) {
  // Area order = first appearance in the catalog.
  const areas: string[] = [];
  for (const p of pages) {
    if (!areas.includes(p.area)) areas.push(p.area);
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Help</h1>
          <div className="page-subtitle">
            All help pages, grouped by area — {pages.length} page
            {pages.length === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      {/* CTSK-383 refinement (operator ask, 2026-08-18): entries are
          indented under a left rule so each area reads as a visual
          group; per-entry count sits beside the area heading. Markup
          contract unchanged (region/aria-label, h2, link-per-slug) —
          HelpIndex.test.tsx pins it. */}
      {areas.map((area) => {
        const entries = pages.filter((p) => p.area === area);
        return (
          <section key={area} aria-label={`Help — ${area}`} style={{ marginBottom: 28 }}>
            {/* Count renders as a SIBLING of the h2, not inside it —
                the heading's textContent is a pinned contract
                (HelpIndex.test.tsx area-order cell). */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '0 0 8px' }}>
              <h2
                style={{
                  fontSize: 13,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--text-muted)',
                  margin: 0,
                }}
              >
                {area}
              </h2>
              <span
                aria-hidden="true"
                style={{
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  opacity: 0.7,
                }}
              >
                {entries.length}
              </span>
            </div>
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: '2px 0 2px 16px',
                borderLeft: '2px solid var(--border-subtle)',
              }}
            >
              {entries.map((p) => (
                <li key={p.slug} style={{ padding: '7px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <a
                      href={`/help/${p.slug}`}
                      onClick={(e) => {
                        if (onNavigate) {
                          e.preventDefault();
                          onNavigate(`/help/${p.slug}`);
                        }
                      }}
                      style={{ color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      {p.title}
                    </a>
                    <code
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-muted)',
                        opacity: 0.8,
                      }}
                    >
                      /help/{p.slug}
                    </code>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, maxWidth: 640 }}>
                    {p.description}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}
